import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { BehaviorSubject, Observable, Subscription, combineLatest, map } from 'rxjs';
import { ChatService } from '../../chat.service';
import { ChatUiService } from '../../chat-ui.service';
import { ChatSoundService } from '../../chat-sound.service';
import { ChatSheetService } from '../../chat-sheet.service';
import { ConversationTone, conversationTone } from '../../ui/conversation-tone';
import { ChatListChip, ChatListKind, LIST_KINDS, listChips, listEmptyText, listRows, typeTag } from '../../ui/conversation-sections';
import { NewChoice, NewChooserComponent } from '../new-chooser/new-chooser.component';
import { NotifySettingsComponent } from '../notify-settings/notify-settings.component';
import { ConversationSummary, SearchHit } from '../../core/hub-types';
import { SUPPORT_NAME } from '../../ui/support-sender';
import { normalise } from '../../core/patient-mentions';
import { PatientService } from '../../../core/services/patient.service';
import { avatarHue, avatarOf, type AvatarView } from '../../core/avatar';
import { BrowseChannelsDialogComponent } from '../browse-channels-dialog/browse-channels-dialog.component';
import { NewConversationDialogComponent, NewConversationResult } from '../new-conversation-dialog/new-conversation-dialog.component';
import { ChatPushService } from '../../chat-push.service';
import { REFERRALS_ENABLED, ReferralService } from '../../../core/services/referral.service';
import { UserService } from '../../../core/services/user.service';
import { ConversationMenuComponent } from '../conversation-menu/conversation-menu.component';

/** The chip picked, kept for this browser session only, so reopening the chat box keeps it. */
const KIND_KEY = 'medicai.chat.listKind';

/**
 * The practice's conversations, inside the floating chat window: one list, newest message first, narrowed by the chips
 * and the search box, each row with its kind and unread count. The practice is the one selected in the app header (ChatService.orgId$).
 */
@Component({
  selector: 'app-chat-conversation-list',
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss'],
})
export class ConversationListComponent implements OnDestroy {
  /** the "Medical professionals" item and section (the referral network): only where referrals are switched on */
  readonly networkOn = REFERRALS_ENABLED;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  readonly filter$ = new BehaviorSubject<string>('');
  private readonly searchRequests: Subscription;
  readonly kind$ = new BehaviorSubject<ChatListKind>(ConversationListComponent.savedKind());

  /** Picks a chip; picking the one already on goes back to All. */
  setKind(kind: ChatListKind): void {
    const next = kind === this.kind$.value ? 'all' : kind;
    this.kind$.next(next);
    try { sessionStorage.setItem(KIND_KEY, next); } catch { /* storage blocked: the choice lasts until reload */ }
  }

  private static savedKind(): ChatListKind {
    try {
      const v = sessionStorage.getItem(KIND_KEY) as ChatListKind | null;
      return v && v in LIST_KINDS ? v : 'all';
    } catch {
      return 'all';
    }
  }

  /**
   * Physicians talk to other clinicians most, so for them "Medical professionals" leads the list (2026-09-22); everyone
   * else keeps the usual order, with it after Referrals.
   */
  get professionalsFirst(): boolean {
    return this.users.isUserPhysician;
  }

  ngOnDestroy(): void {
    this.searchRequests.unsubscribe();
  }

  /** The filter box: it narrows the conversation list at once, and asks the hub for messages a moment after typing stops. */
  onFilter(value: string): void {
    this.filter$.next(value);
    this.searchFor(value);
  }
  readonly vm$: Observable<{
    orgId: string | null;
    ready: boolean;
    online: Record<string, true>;
    /** the patient whose file is open, if any: their conversations are marked */
    openPatientId: string | null;
    /** whether the box has anything in it, so the list can say "nothing matches" rather than "none yet" */
    searching: boolean;
    /** the chips to draw: All, then every kind this practice has any conversation of */
    chips: ChatListChip[];
    kind: ChatListKind;
    /** every conversation shown, newest message first */
    rows: ConversationSummary[];
    /** the practice's chats with medical professionals, to tell two of the same name apart */
    network: ConversationSummary[];
    emptyText: string | null;
  }>;

  constructor(
    readonly chat: ChatService,
    private readonly ui: ChatUiService,
    readonly push: ChatPushService,
    readonly sound: ChatSoundService,
    private readonly sheets: ChatSheetService,
    private readonly patients: PatientService,
    private readonly users: UserService,
    private readonly referrals: ReferralService,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    // A search asked for elsewhere (a patient's file: their name): into the box, across every kind of conversation.
    this.searchRequests = this.ui.state$.subscribe((s) => {
      if (s.search === null) return;
      const text = this.ui.takeSearch();
      if (text === null) return;
      if (this.kind$.value !== 'all') this.setKind('all');
      this.onFilter(text);
    });
    this.vm$ = combineLatest([
      this.chat.orgId$, this.chat.conversations$(), this.chat.store.state$, this.filter$, this.patients.currentPatient$, this.kind$,
    ]).pipe(
      map(([orgId, list, state, filter, openPatient, picked]) => {
        const words = normalise(filter).split(' ').filter(Boolean);
        const visible = words.length ? list.filter((c) => this.matches(c, words)) : list;
        if (this.networkOn && orgId && list.some((c) => c.type === 'network')) this.loadNetworkEmails(orgId);
        const chips = listChips(list, this.professionalsFirst);
        // a chip picked earlier whose kind has since gone falls back to All
        const kind = chips.some((c) => c.kind === picked) ? picked : 'all';
        const searching = words.length > 0;
        const rows = listRows(visible, kind);
        return {
          orgId,
          ready: !!state.me,
          online: state.online,
          openPatientId: openPatient ? String(openPatient.id) : null,
          searching,
          chips,
          kind,
          rows,
          network: list.filter((c) => c.type === 'network'),
          emptyText: rows.length ? null : listEmptyText(kind, searching),
        };
      }),
    );
  }

  /** Whether a row counts as unread, as its red badge counts it (muted chats, and groups not joined, do not). */
  countsUnread(c: ConversationSummary): boolean {
    return c.unread > 0 && !this.muted(c) && (c.type !== 'channel' || c.access === 'participant');
  }

  /**
   * Messages matching what was typed in the filter box (2026-09-20). The box narrows the conversation list as it
   * always did; from two characters it ALSO asks the hub for messages, a moment after typing stops.
   */
  hits: SearchHit[] = [];
  searching = false;
  searchFailed = false;

  private searchFor(query: string): void {
    const text = query.trim();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (text.length < 2) {
      this.hits = [];
      this.searching = false;
      this.searchFailed = false;
      return;
    }
    this.searching = true;
    this.searchFailed = false;
    this.searchTimer = setTimeout(() => {
      const asked = text;
      this.chat.store.search(asked).then(
        (hits) => {
          if (this.filter$.value.trim() !== asked) return; // the box moved on
          // A support answer is from MedicAI Support, not the agent who wrote it.
          const myName = this.chat.store.snapshot.me?.displayName;
          this.hits = hits.map((h) => (h.conversationType === 'support' && h.senderName !== myName ? { ...h, senderName: SUPPORT_NAME } : h));
          this.searching = false;
        },
        () => {
          this.hits = [];
          this.searching = false;
          this.searchFailed = true;
        },
      );
    }, 300);
  }

  /**
   * WHAT THE BOX MATCHES (2026-09-21). Every word must be found somewhere, in any order, ignoring case and accents,
   * so "smith dr jones" finds the referral about Smith with Dr Jones however it is typed.
   *
   * The haystack is the row's own title plus the other side of a referral. A referral's title is already
   * "<patient> · <counterpart>", so searching a doctor lists every patient referred to them — which is the point:
   * refer ten patients to Dr X, search Dr X, and pick the one you meant. The counterpart is added explicitly as well
   * so the search still works if a side has not named itself yet.
   */
  private matches(c: ConversationSummary, words: readonly string[]): boolean {
    const hay = normalise([
      this.chat.store.titleOf(c), c.referral?.counterpart ?? '', c.patientDirect?.patientName ?? '', c.description ?? '',
    ].filter(Boolean).join(' '));
    return words.every((w) => hay.includes(w));
  }

  /**
   * True when this conversation is ABOUT `patientId`: their practice chat, a referral chat about them, or their own
   * chat from the patient app (§8; the hub gives the practice side the patient it is filed on).
   */
  private isAbout(c: ConversationSummary, patientId: string): boolean {
    const mine = c.type === 'patient' ? c.patientRef : c.type === 'patient_direct' ? c.patientDirect?.patientRef : c.referral?.patientRef;
    return !!mine && String(mine).toLowerCase() === patientId.toLowerCase();
  }

  /** Whether to mark this row as being about the patient whose file is open. */
  aboutOpenPatient(c: ConversationSummary, patientId: string | null): boolean {
    return !!patientId && this.isAbout(c, patientId);
  }

  /** Opens the conversation a hit is in, at that message. */
  openHit(hit: SearchHit): void {
    this.ui.openConversation(hit.conversationId);
    void this.chat.store.openAt(hit.conversationId, hit.seq).catch(() => undefined);
  }

  /** The other person's profile photo in a direct message, when they have one; null everywhere else. */
  /** A row's kind, as its chip names it, in the colour of its conversation (patient orange, groups blue, ...). */
  tag(c: ConversationSummary): string {
    return typeTag(c);
  }

  tone(c: ConversationSummary): ConversationTone {
    return conversationTone(c.type);
  }

  /** A group's own photo, or the other person's in a one-to-one. */
  photo(c: ConversationSummary): string | null {
    return this.chat.store.photoUrlOf(c);
  }

  /** The round badge beside a conversation: a person's initials, or an icon for a channel, patient, referral, support. */
  avatar(c: ConversationSummary): AvatarView {
    // A patient's own chat from their app (§8) gets a phone, to tell it apart at a glance from the practice's own
    // chat about the same patient. Set here rather than in chat/core/avatar.ts, which is a synced copy of the hub's.
    if (c.type === 'patient_direct') return { initials: null, icon: 'phone_iphone', hue: avatarHue(c.id) };
    // The standing billing room with the practice's billing team on Connect (2026-10-05).
    if (c.type === 'billing' && c.billing?.kind === 'room') return { initials: null, icon: 'receipt_long', hue: avatarHue(c.id) };
    return avatarOf(c, this.chat.store.snapshot.me?.principalId ?? null);
  }

  title(c: ConversationSummary): string {
    return this.chat.store.titleOf(c);
  }

  /**
   * An external clinician's invite address, by link (from this practice's own network list), to tell apart two
   * medical professionals of the same name (2026-09-22: a chat was opened with the wrong one of two). Loaded once per
   * practice, the first time the list has a network chat.
   */
  private networkEmails = new Map<string, string>();
  private networkEmailsOrg: string | null = null;

  private loadNetworkEmails(orgId: string): void {
    const groupId = this.chat.groupIdOfOrg(orgId);
    if (!groupId || this.networkEmailsOrg === orgId) return;
    this.networkEmailsOrg = orgId;
    this.referrals.networkContacts(groupId).subscribe({
      next: (links) => {
        const emails = new Map<string, string>();
        for (const l of links) for (const p of l.people) if (l.kind === 'external' && p.email) emails.set(l.linkId.toLowerCase(), p.email);
        this.networkEmails = emails;
      },
      error: () => { this.networkEmailsOrg = null; },
    });
  }

  /** The invite address of a network chat's external clinician, when known. */
  networkEmail(c: ConversationSummary): string | null {
    const n = c.type === 'network' ? c.network?.counterpart : undefined;
    return n && n.kind === 'external' ? this.networkEmails.get(n.linkRef.toLowerCase()) ?? null : null;
  }

  /** Shown after a network chat's name when another network chat has the same name: which of the two this is. */
  networkAlias(c: ConversationSummary, network: ConversationSummary[]): string | null {
    const email = this.networkEmail(c);
    if (!email) return null;
    const name = this.title(c).toLowerCase();
    return network.some((o) => o.id !== c.id && this.title(o).toLowerCase() === name) ? email : null;
  }

  isOnline(c: ConversationSummary, online: Record<string, true>): boolean {
    return c.type === 'direct' && !!c.members?.[0] && !!online[c.members[0].principalId];
  }

  muted(c: ConversationSummary): boolean {
    return this.chat.store.isMuted(c);
  }

  /**
   * The row's time, as the referrals dashboard's list shows it: today's time, "Yesterday", a weekday this week, else
   * the date. Empty when nothing has been said yet.
   */
  when(c: ConversationSummary): string {
    if (!c.lastMessageAt) return '';
    const d = new Date(c.lastMessageAt);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const day = 86_400_000;
    if (d.getTime() >= startOfToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (d.getTime() >= startOfToday - day) return 'Yesterday';
    if (d.getTime() >= startOfToday - 6 * day) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  }

  /** The preview line before anything has been said: what the conversation is, so the row is never blank. */
  emptyPreview(c: ConversationSummary): string {
    switch (c.type) {
      case 'patient': return 'Patient chat';
      case 'referral': return c.referral?.counterpart ? `Referral chat with ${c.referral.counterpart}` : 'Referral chat';
      case 'support': return 'Ask MedicAI Support anything';
      case 'network': return c.network?.counterpart.kind === 'external'
        ? `Outside MedicAI · ${this.networkEmail(c) ?? 'medical professional'}` : 'Medical professional';
      case 'channel': return c.description || 'No messages yet';
      case 'billing': return c.billing?.counterpartName ? `MedicAI Connect · ${c.billing.counterpartName}` : 'MedicAI Connect';
      default: return 'No messages yet';
    }
  }

  trackById(_i: number, c: ConversationSummary): string {
    return c.id;
  }

  select(c: ConversationSummary): void {
    this.ui.openConversation(c.id);
  }

  @ViewChild('rowMenu') rowMenu?: ConversationMenuComponent;
  @ViewChild('rowMenuAnchor') rowMenuAnchor?: ElementRef<HTMLElement>;
  @ViewChild(MatMenuTrigger) rowMenuTrigger?: MatMenuTrigger;

  /**
   * A row's own menu (the chat window's conversation menu, for this row): at the pointer on a right-click, under the
   * row on a long press. The row is not opened.
   */
  openRowMenu(c: ConversationSummary, row: HTMLElement, e?: MouseEvent): void {
    e?.preventDefault();
    const anchor = this.rowMenuAnchor?.nativeElement;
    if (!this.rowMenu || !anchor || !this.rowMenuTrigger || this.rowMenuTrigger.menuOpen) return;
    const host = this.host.nativeElement.getBoundingClientRect();
    const box = row.getBoundingClientRect();
    // The anchor is moved here rather than bound, so it is in place before the menu measures it.
    anchor.style.left = `${(e ? e.clientX : box.left + 56) - host.left}px`;
    anchor.style.top = `${(e ? e.clientY : box.bottom) - host.top}px`;
    this.rowMenu.conversation = c;
    this.rowMenu.accent = getComputedStyle(row).getPropertyValue('--sec').trim() || '#009688';
    this.rowMenuTrigger.openMenu();
  }

  /** The "+": what to start (a panel in the window), then that conversation's own panel. */
  startNew(orgId: string): void {
    // The billing team is offered only to a practice that has one on MedicAI Connect (2026-10-05).
    void this.chat.store.billingRelations().catch(() => []).then((relations) => {
      // A state unit has neither colleagues outside it nor a billing team (tasks/state-mode.md).
      const unit = this.chat.isStateUnitOrg(orgId);
      const billingOn = !unit && relations.some((r) => r.orgId === orgId && !r.ended);
      this.sheets.open<NewChoice>(NewChooserComponent, { data: { networkOn: this.networkOn && !unit, billingOn } }).afterClosed().subscribe((choice) => {
        if (choice === 'browse') this.browse(orgId);
        else if (choice === 'support') void this.chat.openSupportChat(this.chat.groupIdOfOrg(orgId)).catch(() => undefined);
        else if (choice) this.newConversation(orgId, choice);
      });
    });
  }

  openNotifySettings(): void {
    this.sheets.open(NotifySettingsComponent);
  }

  newConversation(orgId: string, mode: 'channel' | 'message' | 'network' | 'billing'): void {
    const ref = this.sheets.open<NewConversationResult>(NewConversationDialogComponent, { data: { orgId, mode } });
    ref.afterClosed().subscribe((result?: NewConversationResult) => {
      if (result) this.ui.openConversation(result.conversationId);
    });
  }

  browse(orgId: string): void {
    const ref = this.sheets.open<string>(BrowseChannelsDialogComponent, { data: { orgId } });
    ref.afterClosed().subscribe((id?: string) => {
      if (!id) return;
      // A public channel not yet joined is not in the list: fetch it so it can be read and joined.
      void this.chat.store.refreshConversation(id).then(() => this.ui.openConversation(id), () => undefined);
    });
  }

}
